const BASE = '/api/v1';

/** Send a signed-in person to the server-rendered click-through, then back here. */
export function goToLegalAccept(): void {
  const here = window.location.pathname + window.location.search;
  window.location.replace(`/legal/accept?return_to=${encodeURIComponent(here)}`);
}

async function request<T>(
  path: string,
  options?: RequestInit & { skipAuthRedirect?: boolean }
): Promise<T> {
  const { skipAuthRedirect, ...fetchOptions } = options ?? {};
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...fetchOptions?.headers },
    ...fetchOptions,
  });

  if (res.status === 401) {
    if (!skipAuthRedirect && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
    throw new Error('Unauthorized');
  }

  if (res.status === 403) {
    const body = await res.json().catch(() => ({})) as Record<string, unknown>;
    if (body.error === 'terms_acceptance_required') {
      goToLegalAccept();
      throw new Error(body.message as string);
    }
    if (body.pro_required) {
      const proError = new Error(body.error as string) as Error & { proRequired: true; feature: string };
      proError.proRequired = true;
      proError.feature = body.feature as string;
      throw proError;
    }
    if (body.error === 'idea_limit_reached') {
      const limitError = new Error(body.message as string) as Error & {
        ideaLimitReached: true;
        currentCount: number;
        limit: number;
        upgradeUrl: string;
      };
      limitError.ideaLimitReached = true;
      limitError.currentCount = body.currentCount as number;
      limitError.limit = body.limit as number;
      limitError.upgradeUrl = body.upgradeUrl as string;
      throw limitError;
    }
    throw new Error((body.error as string) ?? 'Forbidden');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((body as { error?: string }).error ?? res.statusText);
  }

  return res.json() as Promise<T>;
}

// Types matching API responses
export interface Idea {
  id: number;
  number: number;
  title: string;
  body: string | null;
  status: 'captured' | 'exploring' | 'building' | 'shipped' | 'shelved';
  source: string;
  source_url: string | null;
  vectorize_id: string | null;
  tags: string[];
  links?: IdeaLink[];
  relations?: IdeaRelation[];
  is_archived: number;
  /** 1 while waiting in the Inbox (P5-04). */
  is_suggested?: number;
  created_at: string;
  updated_at: string;
  processing?: boolean;
  /** GET /ideas/:id only, Pro only (P5-02). */
  recurrence?: { count: number; since: string } | null;
  score?: number | null;
  /** GET /ideas, Pro, building ideas with code only (P5-23). */
  code_activity?: CodeActivity;
}

export interface CodeActivity {
  last_commit_at: string | null;
  days_since_commit: number | null;
  pr: { number: number; state: 'open' | 'merged' | 'closed'; url: string | null } | null;
  stalled: boolean;
}

export interface IdeaLink {
  id: number;
  url: string;
  title: string | null;
  link_type: string;
  created_at: string;
}

export interface IdeaRelation {
  id: number;
  source_idea_id: number;
  target_idea_id: number;
  relation_type: string;
  score: number | null;
  note: string | null;
  created_by: 'system' | 'user';
  related_idea_title?: string;
  related_idea_number?: number;
}

export interface GroupedRelation {
  id: number;
  relation_type: string;
  score: number | null;
  note: string | null;
  created_by: 'system' | 'user';
  created_at: string;
  idea_id: number;
  idea_number: number;
  idea_title: string;
  idea_status: string;
}

export interface GroupedRelations {
  outgoing: GroupedRelation[];
  incoming: GroupedRelation[];
}

export interface DuplicateDetection {
  id: number;
  idea_id: number;
  duplicate_of_id: number;
  similarity_score: number;
  status: string;
  idea_title: string;
  duplicate_title: string;
  idea_number: number;
  duplicate_number: number;
  idea_body: string | null;
  duplicate_body: string | null;
  idea_status: string;
  duplicate_status: string;
  idea_source: string;
  duplicate_source: string;
  idea_created_at: string;
  duplicate_created_at: string;
  created_at: string;
}

export interface Tag {
  id: number;
  name: string;
  color: string | null;
  idea_count: number;
}

export interface User {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  plan: 'free' | 'pro';
  settings_json: string;
  has_anthropic_key: boolean;
  has_openai_key: boolean;
  has_openrouter_key: boolean;
  active_idea_count: number;
  is_trialing: boolean;
  trial_days_remaining: number | null;
  /** UTC `YYYY-MM-DD HH:MM:SS`; kept after the trial lapses. */
  trial_ends_at?: string | null;
  /** Set once the user has been through Stripe checkout. */
  stripe_customer_id?: string | null;
  /** Linked sign-in providers ('github' | 'google' | 'apple' | 'email'); added in P1-13. */
  sign_in_providers?: string[];
  legal?: { acceptance_required: boolean };
  created_at: string;
}

export interface ProRequiredError extends Error {
  proRequired: true;
  feature: string;
}

export function isProRequired(err: unknown): err is ProRequiredError {
  return err instanceof Error && (err as ProRequiredError).proRequired === true;
}

export interface IdeaLimitError extends Error {
  ideaLimitReached: true;
  currentCount: number;
  limit: number;
  upgradeUrl: string;
}

export function isIdeaLimitError(err: unknown): err is IdeaLimitError {
  return err instanceof Error && (err as IdeaLimitError).ideaLimitReached === true;
}

export interface ApiKeyInfo {
  id: string;
  label: string;
  last_used_at: string | null;
  created_at: string;
}

// First-run funnel events (P1-17) → Analytics Engine via POST /events. Fire and
// forget: never blocks the UI, never surfaces an error. Labels must be short
// identifiers (the server refuses free text); never pass idea content or emails.
export type TrackEvent = 'onboarding_step' | 'channel_selected' | 'onboarding_dismissed';

export function track(event: TrackEvent, props?: { step?: string; channel?: string }): void {
  try {
    void fetch(`${BASE}/events`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, props }),
    }).catch(() => {});
  } catch {
    // Analytics must never break the page
  }
}

// User
export const getMe = () => request<User>('/user/me', { skipAuthRedirect: true });
export const updateMe = (data: { display_name?: string; settings_json?: string }) =>
  request<User>('/user/me', { method: 'PATCH', body: JSON.stringify(data) });
/**
 * Change some settings keys (undefined removes one) on top of what the server has now, so two
 * settings sections saving from their own copies can't undo each other's changes.
 */
export async function patchSettings(patch: Record<string, unknown>): Promise<User> {
  let current: Record<string, unknown> = {};
  try {
    current = JSON.parse((await getMe()).settings_json) as Record<string, unknown>;
  } catch {
    current = {};
  }
  const next: Record<string, unknown> = { ...current };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) delete next[key];
    else next[key] = value;
  }
  return updateMe({ settings_json: JSON.stringify(next) });
}
/** Feature flags (P1-14); separate from /user/me so its keys stay unchanged. */
export interface Flags { agent_view: boolean }
export const getFlags = () => request<{ flags: Flags }>('/user/flags');
export const getSupportLink = () => request<{ url: string }>('/user/support-link');
/** Signed-out fallback for the Support links (used when /user/support-link fails). */
export const SUPPORT_PAGE_URL = 'https://support.neuralconfig.com/neuralrepo?from=app&platform=web';

// Ideas
export const listIdeas = (params?: { status?: string; tag?: string; connection_types?: string; limit?: number; offset?: number }) => {
  const sp = new URLSearchParams();
  if (params?.status) sp.set('status', params.status);
  if (params?.tag) sp.set('tag', params.tag);
  if (params?.connection_types) sp.set('connection_types', params.connection_types);
  if (params?.limit) sp.set('limit', String(params.limit));
  if (params?.offset) sp.set('offset', String(params.offset));
  const qs = sp.toString();
  return request<{ ideas: Idea[]; has_more?: boolean }>(`/ideas${qs ? `?${qs}` : ''}`);
};

export const getIdea = (id: number) => request<Idea>(`/ideas/${id}`);

export const createIdea = (data: {
  title: string;
  body?: string;
  tags?: string[];
  source?: string;
  status?: string;
}) => request<Idea>('/ideas', { method: 'POST', body: JSON.stringify(data) });

export const updateIdea = (id: number, data: { title?: string; body?: string; status?: string; tags?: string[] }) =>
  request<Idea>(`/ideas/${id}`, { method: 'PATCH', body: JSON.stringify(data) });

export const archiveIdea = (id: number) =>
  request<{ success: boolean }>(`/ideas/${id}`, { method: 'DELETE' });

export const bulkUpdateIdeas = (data: {
  ids: number[];
  status?: string;
  add_tags?: string[];
  remove_tags?: string[];
}) =>
  request<{
    updated: number;
    errors: number;
    results: Array<{ id: number; status: 'updated' | 'error'; error?: string }>;
  }>('/ideas/bulk', { method: 'PATCH', body: JSON.stringify(data) });

export const searchIdeas = (
  q: string,
  mode?: 'keyword' | 'semantic',
  opts?: { minScore?: number; ftsMode?: string }
) => {
  const params = new URLSearchParams({ q });
  if (mode) params.set('mode', mode);
  if (opts?.minScore != null) params.set('min_score', String(opts.minScore));
  if (opts?.ftsMode) params.set('fts_mode', opts.ftsMode);
  return request<{
    query: string;
    results: Idea[];
    search_type: string;
    fts_mode?: string;
    semantic_limit_reached?: boolean;
    semantic_limit_message?: string;
  }>(
    `/ideas/search?${params.toString()}`
  );
};

// Duplicates
export const listDuplicates = () =>
  request<{ duplicates: DuplicateDetection[] }>('/ideas/duplicates');

export const dismissDuplicate = (id: number) =>
  request<{ success: boolean }>(`/ideas/duplicates/${id}/dismiss`, { method: 'POST' });

export const mergeDuplicate = (id: number) =>
  request<{ success: boolean }>(`/ideas/duplicates/${id}/merge`, { method: 'POST' });

// Review queue
export interface ReviewStaleIdea {
  id: number;
  number: number;
  title: string;
  body: string | null;
  status: string;
  source: string;
  updated_at: string;
  days_stale: number;
  /** Times the theme came back (P5-02); 0 on free. */
  recurrence?: number;
}

/** A captured or shelved idea whose theme keeps coming back (P5-02, Pro). */
export interface ReviewRecurringIdea {
  id: number;
  number: number;
  title: string;
  body: string | null;
  status: string;
  source: string;
  created_at: string;
  recurrence: number;
  recurrence_since: string;
}

export interface ReviewConnection {
  id: number;
  relation_type: string;
  score: number | null;
  created_by: 'system' | 'user';
  created_at: string;
  source_idea_id: number;
  source_number: number;
  source_title: string;
  target_idea_id: number;
  target_number: number;
  target_title: string;
}

/** A status change GitHub activity suggests (P5-07): "PR #12 merged → mark #42 shipped?". */
export interface ReviewCodeSuggestion {
  id: number;
  idea_id: number;
  number: number;
  title: string;
  status: string;
  value: 'building' | 'shipped';
  reason: string | null;
  ref_url: string | null;
  repo_full_name: string | null;
  created_at: string;
}

/** A pull request (P5-16) or issue (P5-13) that mentions no idea but reads like this one. Accept/dismiss with resolveCodeSuggestion. */
export interface ReviewCodeLinkSuggestion {
  id: number;
  idea_id: number;
  number: number;
  title: string;
  status: string;
  /** "owner/repo#88 title" */
  reason: string | null;
  ref_url: string;
  repo_full_name: string | null;
  created_at: string;
  /** Issues only: an issue the idea already links, so the two may be duplicates. */
  idea_issue_url?: string | null;
}

/** A building idea whose repos have had no commits for six weeks (P5-16). */
export interface ReviewStalledIdea {
  id: number;
  number: number;
  title: string;
  repos: string[];
  last_commit_at: string | null;
  days_quiet: number | null;
}

/** An idea saved from a coding session without asking, waiting in the Inbox (P5-04). */
export interface ReviewSuggestedIdea {
  id: number;
  number: number;
  title: string;
  body: string | null;
  source_url: string | null;
  created_at: string;
  tags: string[];
}

export interface ReviewQueue {
  stale: ReviewStaleIdea[];
  connections: ReviewConnection[];
  duplicates: DuplicateDetection[];
  /** Context, not a to-do: not part of counts. */
  recurring?: ReviewRecurringIdea[];
  /** Pro; absent from older servers. */
  code?: ReviewCodeSuggestion[];
  /** Inbox (P5-04), all plans; absent from older servers. */
  suggested?: ReviewSuggestedIdea[];
  /** PRs that read like an idea (P5-16), Pro; absent from older servers. */
  code_links?: ReviewCodeLinkSuggestion[];
  /** Building ideas whose repos went quiet (P5-16), Pro; absent from older servers. */
  stalled?: ReviewStalledIdea[];
  counts: {
    stale: number; connections: number; duplicates: number; code?: number; suggested?: number;
    code_links?: number; stalled?: number; total: number;
  };
  stale_threshold_days: number;
}

export const getReviewQueue = () => request<ReviewQueue>('/review');

export const keepIdea = (id: number) =>
  request<{ success: boolean }>(`/review/${id}/keep`, { method: 'POST' });

/** Keep an Inbox suggestion: it joins the library (403 idea_limit_reached on a full free plan). */
export const keepSuggestion = (id: number) =>
  request<{ success: boolean }>(`/review/suggested/${id}/keep`, { method: 'POST' });

/** Keep a new connection: it leaves the review queue on every device (P3-25). */
export const keepConnection = (relationId: number) =>
  request<{ success: boolean }>(`/review/connections/${relationId}/keep`, { method: 'POST' });

/** Apply (accept) or dismiss a status suggestion from GitHub; either way it's closed for good. */
export const resolveCodeSuggestion = (id: number, action: 'accept' | 'dismiss') =>
  request<{ success: boolean; state: string }>(`/review/suggestions/${id}/${action}`, { method: 'POST' });

/** "Still building" on a stalled idea (P5-16). */
export const keepStalledIdea = (ideaId: number) =>
  request<{ success: boolean; kept: boolean }>(`/review/stalled/${ideaId}/keep`, { method: 'POST' });

/** A status fix the code already shows (P5-22 "Catch up from GitHub"). */
export interface CatchUpRow {
  idea_id: number;
  number: number;
  title: string;
  from: Idea['status'];
  to: Idea['status'];
  evidence: { kind: 'pr_merged' | 'pr_open' | 'stalled'; url: string | null; label: string; at: string | null };
}

export const getGithubCatchUp = () =>
  request<{ pro: boolean; rows: CatchUpRow[]; show_banner: boolean }>('/review/github-catch-up');

export const applyGithubCatchUp = (ideaIds: number[]) =>
  request<{ success: boolean; applied: CatchUpRow[] }>('/review/github-catch-up/apply', {
    method: 'POST',
    body: JSON.stringify({ idea_ids: ideaIds }),
  });

// Timeline (P5-08)

export type TimelineDays = 7 | 30 | 90 | 365;

export interface TimelineLane {
  id: number;
  number: number;
  title: string;
  status: Idea['status'];
  created_at: string;
}

export interface TimelineLaneEvent {
  idea_id: number;
  kind: string;
  from_value: string | null;
  to_value: string | null;
  ref_url: string | null;
  created_at: string;
}

export interface TimelineRepoEvent {
  kind: 'commit' | 'pull_request';
  repo_full_name: string;
  ref: string;
  title: string | null;
  state: string | null;
  additions: number | null;
  deletions: number | null;
  url: string | null;
  occurred_at: string;
  merged_at: string | null;
}

export interface TimelineSession {
  idea_id: number;
  kind: IdeaSessionKind;
  conclusion: string | null;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
}

/** One connected repo's activity in the window (P5-26). Lines are merged PRs'. */
export interface TimelineRepoStat {
  repo_full_name: string;
  prs_opened: number;
  prs_merged: number;
  commits: number;
  additions: number;
  deletions: number;
  ideas_linked: number;
  ideas_shipped: number;
}

/** An idea and one pull request linked to it (P5-26). */
export interface TimelineIdeaPr {
  idea_id: number;
  number: number;
  title: string;
  status: Idea['status'];
  captured_at: string;
  shipped_at: string | null;
  repo_full_name: string;
  ref: string;
  pr_title: string | null;
  url: string;
  state: string | null;
  additions: number | null;
  deletions: number | null;
  opened_at: string;
  merged_at: string | null;
}

export interface Timeline {
  from: string;
  to: string;
  unit: 'day' | 'week';
  /** Commits and pull requests are included (Pro). */
  github: boolean;
  lanes: TimelineLane[];
  events: TimelineLaneEvent[];
  /** Claude sessions on the lanes' ideas (P5-12). */
  sessions: TimelineSession[];
  repo_events: TimelineRepoEvent[];
  buckets: Array<{ start: string; captured: number; shipped: number; prs_merged: number }>;
  latency: Array<{ id: number; number: number; title: string; captured_at: string; shipped_at: string }>;
  /** The repo the view is narrowed to (Pro), or null. */
  repo?: string | null;
  /** Per-repo stats over all repos (Pro, P5-26). */
  repos?: TimelineRepoStat[];
  /** Ideas with their pull requests (Pro, P5-26). */
  idea_prs?: TimelineIdeaPr[];
}

export const getTimeline = (days: TimelineDays, repo?: string | null) =>
  request<Timeline>(`/timeline?days=${days}${repo ? `&repo=${encodeURIComponent(repo)}` : ''}`);

export interface BuildHistoryEvent {
  kind: 'commit' | 'pull_request';
  repo: string;
  ref: string;
  title: string | null;
  state: string | null;
  additions: number | null;
  deletions: number | null;
  url: string | null;
  occurred_at: string;
  merged_at: string | null;
}

export interface BuildHistory {
  events: BuildHistoryEvent[];
  weeks: { start: string; commits: number; prs_merged: number }[];
  repos: { repo: string; commits: number; last_commit_at: string }[];
  totals: { commits: number; prs: number; prs_merged: number };
  last_activity_at: string | null;
}

/** Commits and PRs from connected repos tied to an idea (Pro, P5-09); `history` is null on free. */
export const getIdeaBuildHistory = (id: number) =>
  request<{ github: boolean; history: BuildHistory | null }>(`/ideas/${id}/build-history`);

// Session records (P5-12): what Claude sessions did on an idea.
export type IdeaSessionKind = 'action' | 'local' | 'web';

export interface IdeaSession {
  id: number;
  idea_id: number;
  kind: IdeaSessionKind;
  conclusion: string | null;
  summary: string;
  next_step: string | null;
  changes: string[];
  decisions: string[];
  tests: { ran: number; passed: number; failed: number; notes?: string } | null;
  follow_ups: string[];
  open_questions: string[];
  branch: string | null;
  pr_url: string | null;
  run_url: string | null;
  session_url: string | null;
  claude_session_id: string | null;
  turns: number | null;
  duration_ms: number | null;
  cost_usd: number | null;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
}

export const listIdeaSessions = (ideaId: number) => request<{ sessions: IdeaSession[] }>(`/ideas/${ideaId}/sessions`);

// Tags
export const listTags = () => request<{ tags: Tag[] }>('/tags');
export const createTag = (name: string, color?: string) =>
  request<{ tag: Tag }>('/tags', { method: 'POST', body: JSON.stringify({ name, color }) });
export const deleteTagApi = (id: number) =>
  request<{ success: boolean }>(`/tags/${id}`, { method: 'DELETE' });

// API Keys
export const listApiKeys = () => request<{ api_keys: ApiKeyInfo[] }>('/user/api-keys');
export const createApiKey = (label?: string) =>
  request<{ id: string; key: string; label: string; created_at: string }>('/user/api-keys', {
    method: 'POST',
    body: JSON.stringify({ label }),
  });
export const deleteApiKeyApi = (id: string) =>
  request<{ success: boolean }>(`/user/api-keys/${id}`, { method: 'DELETE' });

// MCP
export interface McpToken {
  id: string;
  label: string;
  scopes: string | null;
  last_used_at: string | null;
  created_at: string;
}

export const listMcpTokens = () =>
  request<{ tokens: McpToken[] }>('/user/mcp-tokens');

// Reachability check for the /mcp/v2 connector: its protected-resource
// document is public (GET /mcp/v2 itself answers 405 by design).
export const testMcpConnection = async (): Promise<{ ok: boolean; error?: string }> => {
  try {
    const res = await fetch('/.well-known/oauth-protected-resource/mcp/v2', { method: 'GET' });
    if (!res.ok) return { ok: false, error: `HTTP ${res.status}` };
    const data = await res.json() as { resource?: string };
    if (!data.resource?.endsWith('/mcp/v2')) return { ok: false, error: 'Unexpected response' };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
};

// BYOK
export type BYOKProvider = 'anthropic' | 'openai' | 'openrouter';

export const saveBYOKKey = (provider: BYOKProvider, apiKey: string) =>
  request<{ success: boolean; provider: string }>(`/user/byok/${provider}`, {
    method: 'PUT',
    body: JSON.stringify({ api_key: apiKey }),
  });

export const removeBYOKKey = (provider: BYOKProvider) =>
  request<{ success: boolean; provider: string }>(`/user/byok/${provider}`, { method: 'DELETE' });

export const testBYOKKey = (provider: BYOKProvider) =>
  request<{ ok: boolean; error?: string }>(`/user/byok/${provider}/test`, { method: 'POST' });

export const getBYOKStatus = () =>
  request<{
    active_provider: BYOKProvider | null;
    has_anthropic_key: boolean;
    has_openai_key: boolean;
    has_openrouter_key: boolean;
  }>('/user/byok/status');

// Shortcuts
export interface ShortcutUrls {
  quick_capture_url: string;
}

export const getShortcutUrls = () => request<ShortcutUrls>('/user/shortcuts');

/** A GitHub account (user or org) connected through the NeuralRepo GitHub App (P5-06). */
export interface GithubInstallationInfo {
  installation_id: number;
  account_login: string;
  account_type: string;
  repository_selection: string;
  suspended: boolean;
  connected_at: string;
  backfilled_at: string | null;
  repos: number;
  events: number;
  last_event_at: string | null;
}

export interface GithubStatus {
  /** False until the GitHub App is set up on this server. */
  available: boolean;
  pro: boolean;
  installations: GithubInstallationInfo[];
}

export const getGithubStatus = () => request<GithubStatus>('/integrations/github');
export const connectGithub = () => request<{ url: string }>('/integrations/github/connect', { method: 'POST' });
export const disconnectGithub = (installationId: number) =>
  request<{ disconnected: boolean; uninstalled: boolean }>(`/integrations/github/${installationId}`, { method: 'DELETE' });

/** Repos the user's GitHub installations reach, for "Open issue" (P5-10, Pro). */
export const listGithubRepos = () => request<{ repos: Array<{ full_name: string }> }>('/integrations/github/repos');

/** Claude builds setup per repo (P5-11a, Pro). */
export interface BuildRepo {
  repo: string;
  state: 'pr_open' | 'enabled' | 'disabled';
  setup_pr_url: string | null;
  auto_build: boolean;
  updated_at: string;
}

export const listBuildRepos = () => request<{ repos: BuildRepo[] }>('/integrations/github/builds');
/** Turning builds on by hand when the NeuralRepo app only reads the repo (P5-17). */
export interface BuildSetupSteps {
  repo: string | null;
  path: string;
  label: string;
  yaml: string;
  new_file_url: string | null;
  labels_url: string | null;
  steps: string[];
  gh_commands: string[];
}

export const enableClaudeBuilds = (repo: string) =>
  request<{ repo: BuildRepo; existing: boolean; manual?: undefined } | { repo: null; existing: false; manual: BuildSetupSteps }>(
    '/integrations/github/builds',
    { method: 'POST', body: JSON.stringify({ repo }) },
  );
export const disableClaudeBuilds = (repo: string) =>
  request<{ repo: BuildRepo }>('/integrations/github/builds/disable', { method: 'POST', body: JSON.stringify({ repo }) });
export const getBuildWorkflow = () => request<{ path: string; label: string; yaml: string }>('/integrations/github/builds/workflow');

/** A Claude build of an idea (P5-11b, Pro). */
export interface IdeaBuild {
  id: number;
  idea_id: number;
  repo: string;
  issue_number: number;
  issue_url: string;
  status: 'requested' | 'queued' | 'in_progress' | 'completed';
  conclusion: string | null;
  run_url: string | null;
  pr_url: string | null;
  session_id: number | null;
  requested_at: string;
  started_at: string | null;
  completed_at: string | null;
}

/** Starting a build by hand (P5-17): add the label to the issue, or open the pre-filled one. */
export interface BuildManualStep {
  repo: string;
  label: string;
  issue_url: string | null;
  new_issue: IssueDraft | null;
  gh_command: string;
}

export const startClaudeBuild = (ideaId: number, repo?: string) =>
  request<{ build: IdeaBuild | null; existing: boolean; manual?: BuildManualStep }>(`/ideas/${ideaId}/build`, { method: 'POST', body: JSON.stringify(repo ? { repo } : {}) });
export const listIdeaBuilds = (ideaId: number) => request<{ builds: IdeaBuild[] }>(`/ideas/${ideaId}/builds`);
export const listRecentBuilds = () => request<{ builds: IdeaBuild[] }>('/integrations/github/builds/recent');

/** An issue for the user to open on GitHub: the app only reads the repo (P5-17). */
export interface IssueDraft {
  repo: string;
  title: string;
  body: string;
  labels: string[];
  url: string;
  gh_command: string;
}

export interface OpenedIssue {
  /** Null when NeuralRepo couldn't open it itself; `manual` is then set. */
  issue: { url: string; number: number; repo: string } | null;
  /** True when the idea already had an issue in that repo (nothing new was opened). */
  existing: boolean;
  manual?: IssueDraft;
}

export const openIdeaIssue = (ideaId: number, input: { repo: string; title?: string; body?: string }) =>
  request<OpenedIssue>(`/ideas/${ideaId}/issue`, { method: 'POST', body: JSON.stringify(input) });

export interface EmailCapture {
  address: string | null;
  bare_address: string;
  bare_address_opt_in: boolean;
  bare_address_open: boolean;
  bare_address_sunset: string;
}

export const getEmailCapture = () => request<EmailCapture>('/user/email-capture');

export const rotateEmailCapture = () =>
  request<EmailCapture>('/user/email-capture/rotate', { method: 'POST' });

export const setBareAddressOptIn = (optIn: boolean) =>
  request<EmailCapture>('/user/email-capture/bare-address', {
    method: 'PUT',
    body: JSON.stringify({ opt_in: optIn }),
  });

// Test digest
export const sendTestDigest = () =>
  request<{ success: boolean; content: string }>('/user/test-digest', { method: 'POST' });

// Develop idea
export interface DevelopResult {
  spec: string;
  provider: string;
  model: string;
  usage?: { input_tokens: number; output_tokens: number };
}

export const developIdea = (id: number) =>
  request<DevelopResult>(`/ideas/${id}/develop`, { method: 'POST' });

// Mind map
export interface MapNode {
  id: number;
  number: number;
  title: string;
  status: string;
  tags: string[];
  connectionCount: number;
  created_at: string;
}

export interface MapEdge {
  id: number;
  source: number;
  target: number;
  type: string;
  score: number | null;
  note: string | null;
  created_by: 'system' | 'user';
  created_at: string;
}

export interface MapData {
  nodes: MapNode[];
  edges: MapEdge[];
}

export const getMapData = (opts?: { since?: string; until?: string }) => {
  const params = new URLSearchParams();
  if (opts?.since) params.set('since', opts.since);
  if (opts?.until) params.set('until', opts.until);
  const qs = params.toString();
  return request<MapData>(`/map${qs ? `?${qs}` : ''}`);
};

export interface MapCluster {
  ids: number[];
  signature: string;
  /** Null until the label job has named it. */
  label: string | null;
}

/** Commits per day and recently active repo-linked ideas, for the map overlay (Pro, P5-09). */
export interface MapActivity {
  github: boolean;
  days: { date: string; commits: number }[];
  ideas: { id: number; commits: number; last_commit_at: string }[];
}

export const getMapActivity = () => request<MapActivity>('/map/activity');

/** Named clusters (Pro, P3-12). `pending` while labels are still being written. */
export const getMapClusters = () =>
  request<{ clusters: MapCluster[]; pending: boolean }>('/map/clusters');

export const createRelation = (sourceIdeaId: number, targetIdeaId: number, relationType?: string, note?: string) =>
  request<{ relation: IdeaRelation }>('/map/relations', {
    method: 'POST',
    body: JSON.stringify({
      source_idea_id: sourceIdeaId,
      target_idea_id: targetIdeaId,
      relation_type: relationType ?? 'related',
      ...(note ? { note } : {}),
    }),
  });

export const updateRelation = (id: number, data: { relation_type?: string; note?: string | null }) =>
  request<{ relation: IdeaRelation }>(`/map/relations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

export const deleteRelation = (id: number) =>
  request<{ success: boolean }>(`/map/relations/${id}`, { method: 'DELETE' });

export const getIdeaRelations = (id: number) =>
  request<GroupedRelations>(`/ideas/${id}/relations`);

export const mergeIdeas = (keepId: number, absorbId: number) =>
  request<Idea>(`/ideas/${keepId}/merge`, {
    method: 'POST',
    body: JSON.stringify({ absorb_id: absorbId }),
  });

// Billing
export const checkout = (plan: 'monthly' | 'yearly') =>
  request<{ url: string }>('/user/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({ plan }),
  });

export const billingPortal = () =>
  request<{ url: string }>('/user/billing/portal', { method: 'POST' });

// Export
export const exportData = () => request<Record<string, unknown>>('/user/export', { method: 'POST' });

// Import (mirrors ApiImport* in packages/shared)
export type ImportBody =
  | { format: 'json' | 'csv'; content: string }
  | { format: 'markdown'; files: { name: string; content: string }[] }
  | { format: 'zip'; base64: string };

export interface ImportIssue { ref: string; message: string }
export interface ImportDuplicate { ref: string; title: string; idea_number?: number; of_ref?: string }

export interface ImportPreview {
  parsed: number;
  would_import: { title: string; ref: string }[];
  duplicates: ImportDuplicate[];
  skipped_cap: { ref: string; title: string }[];
  errors: ImportIssue[];
  warnings: ImportIssue[];
  free_room: number | null;
  limit: number | null;
  upgrade_url?: string;
}

export interface ImportResult {
  import_id: string;
  imported: number;
  duplicates: ImportDuplicate[];
  skipped_cap: { ref: string; title: string }[];
  errors: ImportIssue[];
  warnings: ImportIssue[];
  upgrade_url?: string;
}

export interface ImportRun {
  id: string;
  format: 'json' | 'csv' | 'markdown' | 'zip';
  status: 'running' | 'done' | 'failed' | 'undone';
  created_at: string;
  finished_at: string | null;
  counts: { parsed: number; imported: number; duplicates: number; skipped_cap: number; errors: number } | null;
}

export const previewImport = (body: ImportBody) =>
  request<ImportPreview>('/import/preview', { method: 'POST', body: JSON.stringify(body) });

export const runImport = (body: ImportBody) =>
  request<ImportResult>('/import', { method: 'POST', body: JSON.stringify(body) });

export const listImports = () => request<{ imports: ImportRun[] }>('/import');

export const undoImport = (id: string) =>
  request<{ success: boolean }>(`/import/${encodeURIComponent(id)}`, { method: 'DELETE' });

export interface ArchivedIdea {
  id: number;
  number: number;
  title: string;
  updated_at: string;
}

export const listArchivedIdeas = () =>
  request<{ ideas: ArchivedIdea[]; count: number }>('/ideas/archived');

export const purgeArchivedIdeas = () =>
  request<{ success: boolean; ideas_deleted: number }>('/ideas/archived', { method: 'DELETE' });

export const restoreIdea = (id: number) =>
  request<Idea>(`/ideas/${id}/restore`, { method: 'POST' });

export const deleteIdeaPermanently = (id: number) =>
  request<{ success: boolean; ideas_deleted: number }>(`/ideas/${id}/permanent`, { method: 'DELETE' });

export const deleteAccount = (confirmEmail: string) =>
  request<{ success: boolean; out_of_reach: string[] }>('/user/me', {
    method: 'DELETE',
    body: JSON.stringify({ confirm_email: confirmEmail }),
  });

export const exportCsv = async (): Promise<Blob> => {
  const res = await fetch(`${BASE}/user/export/csv`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Export failed');
  return res.blob();
};

export const exportMarkdown = async (): Promise<Blob> => {
  const res = await fetch(`${BASE}/user/export/markdown`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Export failed');
  return res.blob();
};

// Admin
export interface AdminStats {
  users: { total: number; free: number; pro: number; trialing: number };
  ideas: {
    total: number;
    archived: number;
    last_7_days: number;
    last_30_days: number;
    by_status: Record<string, number>;
    by_source: Record<string, number>;
  };
  vectorize: { total: number; vectorized: number; coverage_pct: number };
  tags: { total: number };
  relations: { total: number };
  duplicates: Record<string, number>;
  attachments: { total: number; total_bytes: number };
  sessions: { active: number };
  api_keys: { total: number };
  limits: {
    d1_max_db_size_gb: number;
    d1_max_rows: number;
    vectorize_max_records: number;
    estimated_d1_rows: number;
  };
}

export interface AdminUser {
  id: string;
  email: string;
  display_name: string | null;
  plan: string;
  trial_ends_at: string | null;
  trial_days_remaining: number | null;
  has_stripe: boolean;
  has_subscription: boolean;
  created_at: string;
  idea_count: number;
  archived_count: number;
  tag_count: number;
  relation_count: number;
  storage_bytes: number;
  last_session: string | null;
  last_api_use: string | null;
}

export interface AdminStripeStats {
  mrr: number;
  revenue_30d: number;
  revenue_all_time: number;
  active_subscriptions: number;
  monthly: number;
  yearly: number;
  trials: {
    active: number;
    converted: number;
    expired: number;
    conversion_rate: number;
  };
  failed_payments_30d: number;
  recent_events: Array<{
    id: number;
    type: string;
    amount_cents: number | null;
    currency: string;
    email: string | null;
    created_at: string;
  }>;
}

export interface AdminActivityStats {
  signups_7d: number;
  signups_30d: number;
  dau: number;
  wau: number;
  mau: number;
  ideas_per_day: Array<{ day: string; count: number }>;
  signups_per_day: Array<{ day: string; count: number }>;
}

export interface AdminCloudflareStats {
  available: boolean;
  error?: string;
  hourly?: Array<{
    hour: string;
    requests: number;
    errors: number;
    subrequests: number;
    cpu_p50: number;
    cpu_p99: number;
    duration_p50: number;
    duration_p99: number;
  }>;
  summary_7d?: {
    requests: number;
    errors: number;
    subrequests: number;
    cpu_p50: number;
    cpu_p99: number;
    duration_p50: number;
    duration_p99: number;
  } | null;
}

export const getAdminStats = () => request<AdminStats>('/admin/stats');
export const getAdminUsers = () => request<{ users: AdminUser[] }>('/admin/users');
export const getAdminStripeStats = () => request<AdminStripeStats>('/admin/stats/stripe');
export const getAdminActivityStats = () => request<AdminActivityStats>('/admin/stats/activity');
export const getAdminCloudflareStats = () => request<AdminCloudflareStats>('/admin/stats/cloudflare');
export const updateAdminUserPlan = (userId: string, plan: 'free' | 'pro') =>
  request<{ user: AdminUser }>(`/admin/users/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify({ plan }),
  });

// Agent artifacts
export type Artifact =
  | { type: 'idea_grid'; id: string; ideas: Idea[]; query?: string; pinned?: boolean }
  | { type: 'idea_card'; id: string; idea: Idea; mode: 'created' | 'updated' | 'detail'; pinned?: boolean }
  | { type: 'mini_graph'; id: string; nodes: MapNode[]; edges: MapEdge[]; highlightIds?: number[]; title?: string; pinned?: boolean }
  | { type: 'mermaid'; id: string; code: string; title?: string; pinned?: boolean }
  | { type: 'brainstorm_cluster'; id: string; prompt: string; suggestions: { title: string; body: string }[]; pinned?: boolean }
  | { type: 'comparison'; id: string; ideaA: Idea; ideaB: Idea; similarity: number; pinned?: boolean }
  | { type: 'tag_cloud'; id: string; tags: { name: string; score: number; applied?: boolean }[]; ideaId?: number; ideaNumber?: number; pinned?: boolean }
  | { type: 'summary'; id: string; themes: string[]; summary: string; ideaIds: number[]; pinned?: boolean }
  | {
      type: 'insights';
      id: string;
      stats: { label: string; value: number | string }[];
      breakdown?: { byStatus?: Record<string, number>; bySource?: Record<string, number> };
      topTags?: { name: string; count: number }[];
      mostConnected?: { id: number; title: string; connections: number }[];
      period?: string;
      pinned?: boolean;
    }
  | { type: 'connection_line'; id: string; sourceId: number; targetId: number; relationType: string; pinned?: boolean };

// Agent chat types
export interface AgentChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  reasoning?: string;
  toolCalls?: AgentToolCall[];
}

export interface AgentToolCall {
  tool: string;
  toolCallId: string;
  args: unknown;
  artifactId: string;
  status: 'running' | 'complete' | 'error';
  result?: unknown;
  error?: string;
}
